import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, map, Observable, of, take, tap, throwError } from 'rxjs';
import { CreateUserRequest, CreateUserResponse, UpdateUserRequest, User, UserStats } from '../models/user.model';
import { PaginatedResponse } from '../models/api-response.model';
import { environment } from '../../../environments/environment';

export interface UserGroupsResponse {
  userId: string;
  username: string;
  groups: string[];
  managedGroups: string[];
}

export interface Group {
  id: string;
  name: string;
  path: string;
  subGroups?: Group[];
}

export interface UserGroup {
  id: string;
  name: string;
  path: string;
}

export interface UserGroupsForUserResponse {
  groups: UserGroup[];
  totalGroups: number;
  message: string;
  userId: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/users`;
  private keycloakBaseUrl = `${environment.keycloackBaseUrl}`;
  private apiGetAllUsers = `${environment.apiUrl}/users/all`;
  private apiGetAllUsersV2 = `${environment.apiUrl}/users/v2/all`;
  private apiGetMyGroupsUsers = `${environment.apiUrl}/users/my-groups-users`;

  /** Durée de vie du cache en millisecondes (30 secondes) */
  private readonly CACHE_TTL = 30_000;

  /**
   * Structure interne du cache :
   * - clé : combinaison des paramètres de requête (page, size, search, status)
   * - valeur : { data, timestamp }
   */
  private cacheMap = new Map<string, { data: PaginatedResponse<User>; timestamp: number }>();

  /**
 * BehaviorSubject qui émet la dernière réponse paginée.
 * Il permet aux composants de s'abonner et de recevoir immédiatement la valeur en cache,
 * ou d'attendre un rechargement si le cache est expiré.
 */
  private usersSubject = new BehaviorSubject<PaginatedResponse<User> | null>(null);
  public users$ = this.usersSubject.asObservable();

  /**
   * Indique si une requête de chargement est en cours (pour éviter les doublons).
   */
  private loading = false;

  constructor(private http: HttpClient) { }

  // createUser(userData: CreateUserRequest): Observable<User> {
  //   return this.http.post<User>(`${this.apiUrl}`, userData);
  // }

  createUser(userData: CreateUserRequest): Observable<CreateUserResponse> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    return this.http.post<CreateUserResponse>(`${this.apiUrl}`, userData, { headers })
      .pipe(
        catchError(error => {
          console.error('❌ Erreur création utilisateur:', error);
          return throwError(() => error);
        })
      );
  }

  updateUser(userId: string, userData: UpdateUserRequest): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${userId}`, userData);
  }

  /**
   * Récupère tous les utilisateurs (pour les super admins)
   */
  getUsers(page: number = 0, size: number = 10, search?: string, emailVerified?: boolean, managedGroups?: string[]): Observable<PaginatedResponse<User>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    if (emailVerified !== undefined && emailVerified !== null) {
      params = params.set('emailVerified', emailVerified.toString());
    }

    if (managedGroups && managedGroups.length > 0) {
      params = params.set('managedGroups', managedGroups.join(','));
    }

    return this.http.get<PaginatedResponse<User>>(this.apiGetAllUsers, { params })
      .pipe(
        catchError(error => {
          console.error('Erreur lors du chargement des utilisateurs:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Récupère les utilisateurs des groupes de l'admin connecté
   * Utilise maintenant le nouveau endpoint /my-groups-users
   */
  getUsersFromMyGroups(first: number = 0, max: number = 10, search?: string): Observable<PaginatedResponse<User>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<PaginatedResponse<User>>(this.apiGetMyGroupsUsers, { params })
      .pipe(
        catchError(error => {
          console.error(' Erreur lors du chargement des utilisateurs des groupes:', error);
          console.error('URL:', error.url);
          console.error('Status:', error.status);
          return throwError(() => error);
        })
      );
  }

  /**
 * Récupère les utilisateurs via le nouveau endpoint unifié V2.
 * - SUPER_ADMIN  : tous les utilisateurs du realm
 * - IES_ADMIN    : utilisateurs des sous-groupes de son IES (lu depuis le claim "ies" du token)
 */
  /**
   * Récupère la liste des utilisateurs (V2) avec gestion de cache.
   * - Si une réponse en cache est encore fraîche, elle est émise immédiatement.
   * - Sinon, une requête HTTP est exécutée et le cache est mis à jour.
   *
   * Les composants peuvent s'abonner à `users$` pour recevoir les données en continu.
   */
  getAllUsersV2(
    page: number = 0,
    size: number = 20,
    search?: string,
    status?: string
  ): Observable<PaginatedResponse<User>> {
    const key = this.buildCacheKey(page, size, search, status);

    // Vérifier si une copie fraîche existe
    const cached = this.cacheMap.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      // Mettre à jour le BehaviorSubject avec les données en cache
      this.usersSubject.next(cached.data);
      // Retourner les données directement (synchrone)
      return of(cached.data);
    }

    // Si une requête est déjà en cours, on attend sa réponse
    if (this.loading) {
      return this.users$.pipe(
        filter((data) => data !== null),
        take(1)
      ) as Observable<PaginatedResponse<User>>;
    }

    // Sinon, déclencher le chargement
    this.loading = true;

    const first = page * size;
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', size.toString());
    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status !== 'all') {
      params = params.set('status', status);
    }

    return this.http
      .get<PaginatedResponse<User>>(this.apiGetAllUsersV2, { params })
      .pipe(
        tap((response) => {
          // Mise en cache
          this.cacheMap.set(key, {
            data: response,
            timestamp: Date.now(),
          });
          // Émettre via le BehaviorSubject
          this.usersSubject.next(response);
          this.loading = false;
        }),
        catchError((error) => {
          this.loading = false;
          // En cas d'erreur, on ne met pas à jour le cache
          // On peut éventuellement propager l'erreur
          return throwError(() => error);
        })
      );
  }

  /**
   * Invalide tout le cache (à appeler après création, mise à jour, suppression,
   * changement de statut, etc.).
   */
  invalidateCache(): void {
    this.cacheMap.clear();
    // On peut aussi émettre null pour indiquer que le cache est vide
    // this.usersSubject.next(null);
    // Mais on préfère laisser le subject avec la dernière valeur,
    // car les composants pourront la voir jusqu'au prochain rechargement.
  }

  /**
   * Invalide une entrée spécifique du cache (si on veut être plus granulaire).
   */
  invalidateCacheEntry(page: number, size: number, search?: string, status?: string): void {
    const key = this.buildCacheKey(page, size, search, status);
    this.cacheMap.delete(key);
  }

  // ─── MÉTHODE PRIVÉE ─────────────────────────────────────────────────────

  private buildCacheKey(page: number, size: number, search?: string, status?: string): string {
    return `${page}-${size}-${search || ''}-${status || ''}`;
  }

  getUserById(id: string): Observable<User> {
    /*  let params = new HttpParams()
        .set('clientId', clientId.toString());*/

    return this.http.get<User>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(error => {
          console.error('Erreur lors du chargement de l\'utilisateur:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Méthode unifiée pour activer/désactiver un utilisateur
   */
  toggleUserStatus(userId: string, enabled: boolean): Observable<User> {
    const params = new HttpParams().set('enabled', enabled.toString());

    return this.http.put<User>(`${this.apiUrl}/${userId}/toggle-status`, null, { params })
      .pipe(
        map(response => {
          if (!response) {
            throw new Error('Réponse serveur invalide');
          }

          if (response.enabled !== enabled) {
            console.warn(`Incohérence détectée: attendu ${enabled}, reçu ${response.enabled}`);
          }

          return response;
        }),
        catchError(error => {
          console.error('Erreur lors du changement de statut:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Méthodes legacy pour compatibilité
   */
  activateUser(id: string): Observable<User> {
    return this.toggleUserStatus(id, true);
  }

  deactivateUser(id: string): Observable<User> {
    return this.toggleUserStatus(id, false);
  }

  updateUserInLocalStorage(updatedUser: any): void {
    try {
      const cachedData = localStorage.getItem('users_cache');

      if (cachedData) {
        const cache = JSON.parse(cachedData);

        if (cache.users && Array.isArray(cache.users)) {
          const userIndex = cache.users.findIndex((user: any) => user.id === updatedUser.id);

          if (userIndex !== -1) {
            cache.users[userIndex] = {
              ...cache.users[userIndex],
              ...updatedUser
            };

            cache.cacheTimestamp = Date.now();
            localStorage.setItem('users_cache', JSON.stringify(cache));
            // console.log('Utilisateur mis à jour dans le cache localStorage');
          } else {
            console.warn('Utilisateur non trouvé dans le cache');
          }
        }
      } else {
        console.warn('Aucun cache trouvé dans localStorage');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du cache:', error);
    }
  }

  /**
   * Envoie un email de réinitialisation de mot de passe
   */
  sendResetPasswordEmail(userId: string, actions: string[] = ['UPDATE_PASSWORD']): Observable<void> {
    const access_token = localStorage.getItem('access_token');

    if (!access_token) {
      return throwError(() => new Error('No access token found'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${access_token}`,
      'Content-Type': 'application/json'
    });

    const url = `${this.keycloakBaseUrl}/admin/realms/sigesr/users/${userId}/execute-actions-email`;

    return this.http.put<void>(url, actions, {
      headers: headers,
      params: {
        lifespan: '43200'
      }
    });
  }

  /**
 * Récupère tous les groupes disponibles
 * Note: Cet endpoint doit être créé côté backend
 * URL attendue: GET /api/groups ou /api/habilitation/groups
 */
  // getAllGroups(): Observable<Group[]> {
  //   const token = localStorage.getItem('access_token');
  //   const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

  //   // TODO: Adapter l'URL selon votre configuration backend
  //   // Si l'endpoint n'existe pas encore, créez-le ou utilisez
  //   // une alternative comme récupérer tous les groupes de Keycloak
  //   return this.http.get<Group[]>(
  //     `${environment.apiUrl}/groups`, // ou `${environment.keycloakUrl}/admin/realms/${realm}/groups`
  //     { headers }
  //   );
  // }

  /**
   * Récupère les groupes d'un utilisateur
   */
  getUserGroups(userId: string): Observable<UserGroup[]> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<UserGroup[]>(
      `${this.apiUrl}/${userId}/groups`,
      { headers }
    );
  }

  /**
   * Ajoute un utilisateur à un groupe
   */
  addUserToGroup(userId: string, groupName: string): Observable<any> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${token}`)
      .set('Content-Type', 'application/json');

    return this.http.put(
      `${this.apiUrl}/${userId}/groups/${groupName}`,
      {},
      { headers }
    );
  }

  /**
   * Retire un utilisateur d'un groupe
   */
  removeUserFromGroup(userId: string, groupName: string): Observable<any> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.delete(
      `${this.apiUrl}/${userId}/groups/${groupName}`,
      { headers }
    );
  }

  /**
   * Aplatit la hiérarchie des groupes pour la recherche
   */
  flattenGroups(groups: Group[]): Group[] {
    const result: Group[] = [];

    const flatten = (groupList: Group[]) => {
      groupList.forEach(group => {
        result.push(group);
        if (group.subGroups && group.subGroups.length > 0) {
          flatten(group.subGroups);
        }
      });
    };

    flatten(groups);
    return result;
  }

  /**
   * Normalise le chemin d'un groupe
   */
  normalizeGroupPath(path: string): string {
    if (!path) return '';
    return path.replace(/^\/+|\/+$/g, '');
  }

  /**
 * Récupère tous les groupes disponibles depuis Keycloak
 * Utilise l'API Admin Keycloak directement
 */
  getAllGroups(): Observable<Group[]> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    // URL vers l'API Admin Keycloak pour récupérer tous les groupes
    const url = `${environment.keycloackBaseUrl}/admin/realms/${environment.keycloakConfig.realm || 'sigesr'}/groups`;

    return this.http.get<Group[]>(url, { headers }).pipe(
      // tap(groups => console.log('✅ Groupes récupérés:', groups.length)),
      catchError(error => {
        console.error(' Erreur récupération groupes:', error);

        // Fallback: essayer via votre API backend si disponible
        const backendUrl = `${environment.apiUrl}/groups`;
        return this.http.get<Group[]>(backendUrl, { headers }).pipe(
          catchError(backendError => {
            console.error('Erreur fallback backend:', backendError);
            return throwError(() => new Error('Impossible de récupérer les groupes'));
          })
        );
      })
    );
  }

  /**
   * Récupère les groupes d'un utilisateur spécifique
   * GET /api/users/{userId}/groups
   * La réponse backend est { groups: [...], totalGroups, message, userId }
   */
  getUserGroupsForUser(userId: string): Observable<UserGroup[]> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<UserGroupsForUserResponse>(`${this.apiUrl}/${userId}/groups`, { headers }).pipe(
      map(response => response.groups || []),
      // tap(groups => console.log('✅ Groupes utilisateur récupérés:', groups)),
      catchError(error => {
        console.error('❌ Erreur récupération groupes utilisateur:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Récupère les groupes de l'admin connecté
   * GET /api/users/my-groups
   */
  getMyGroups(): Observable<UserGroupsResponse> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<UserGroupsResponse>(
      `${this.apiUrl}/my-groups`,
      { headers }
    ).pipe(
      // tap(response => console.log('Groupes admin récupérés:', response)),
      catchError(error => {
        console.error('❌ Erreur récupération groupes admin:', error);
        return throwError(() => error);
      })
    );
  }

  getSigleByCode(code: string): Observable<string> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    // Important : responseType: 'text' pour éviter le parsing JSON
    return this.http.get(`${environment.apiUrl}/groups/sigle-by-code/${code}`, {
      headers,
      responseType: 'text'
    });
  }

  getStats(): Observable<UserStats> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<UserStats>(`${this.apiUrl}/stats`, { headers });
  }

  /**
 * Envoie un email de réinitialisation du mot de passe via le backend
 */
  resetPassword(userId: string): Observable<any> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.put(`${this.apiUrl}/${userId}/reset-password`, null, { headers });
  }

  /**
 * Envoie un email de vérification de l'adresse email via le backend
 */
  sendVerificationEmail(userId: string): Observable<any> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.put(`${this.apiUrl}/${userId}/send-verification`, null, { headers });
  }

  /**
  * Change le mot de passe de l'utilisateur actuellement connecté.
  * Le userId est déduit côté backend à partir du token JWT.
  */
  changeMyPassword(request: ChangePasswordRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/me/change-password`, request);
  }

  /** Map {code → sigle} pour toute la hiérarchie (cache backend). */
  getSigleCodeMap(): Observable<Record<string, string>> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<Record<string, string>>(
      `${environment.apiUrl}/groups/sigle-code-map`, { headers }
    );
  }
}